import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Spin } from 'antd';
import { supabase } from '../config/supabase';
import { updatePassword } from '../auth/passwordRecovery';

const { Title, Text, Paragraph } = Typography;

export interface ResetPasswordPageLabels {
  invalidLink: string;
  newPassword: string;
  enterNewPassword: string;
  confirmPassword: string;
  confirmYourPassword: string;
  savePassword: string;
  mismatch: string;
  tooShort: string;
  updatedSignIn: string;
}

const DEFAULT_LABELS: ResetPasswordPageLabels = {
  invalidLink:
    'This reset link is invalid or has expired. Request a new one from the sign-in page, or contact your dispatcher / UBUS support.',
  newPassword: 'New password',
  enterNewPassword: 'Enter a new password',
  confirmPassword: 'Confirm password',
  confirmYourPassword: 'Confirm your password',
  savePassword: 'Save password',
  mismatch: 'Passwords do not match',
  tooShort: 'Password must be at least 8 characters',
  updatedSignIn: 'Password updated. Please sign in.',
};

export interface ResetPasswordPageProps {
  /** Where to send the user after a successful password update */
  successRedirect?: string;
  titleColor?: string;
  primaryColor?: string;
  portalLabel?: string;
  labels?: Partial<ResetPasswordPageLabels>;
}

/**
 * Landing page for Supabase recovery / invite links.
 * Must be registered in Auth redirect allow-list as `{origin}/reset-password`.
 */
export function ResetPasswordPage({
  successRedirect = '/login',
  titleColor,
  primaryColor,
  portalLabel = 'Set a new password',
  labels: labelsProp,
}: ResetPasswordPageProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [ready, setReady] = useState(false);
  const [canReset, setCanReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || (session && event === 'SIGNED_IN')) {
        setCanReset(true);
      }
      setReady(true);
    });

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session) setCanReset(true);
      setReady(true);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (values: { password: string; confirm: string }) => {
    if (values.password !== values.confirm) {
      message.error(labels.mismatch);
      return;
    }
    if (values.password.length < 8) {
      message.error(labels.tooShort);
      return;
    }
    setLoading(true);
    try {
      const { error } = await updatePassword(values.password);
      if (error) {
        message.error(error.message);
        return;
      }
      message.success(labels.updatedSignIn);
      await supabase.auth.signOut();
      navigate(successRedirect, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const buttonStyle = primaryColor
    ? { background: primaryColor, borderColor: primaryColor }
    : undefined;

  if (!ready) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: 16,
      }}
    >
      <Card style={{ width: 400, maxWidth: '100%' }}>
        <Title level={3} style={{ marginBottom: 8, ...(titleColor ? { color: titleColor } : {}) }}>
          UBUS
        </Title>
        <Text type="secondary">{portalLabel}</Text>

        {!canReset ? (
          <Paragraph style={{ marginTop: 24 }}>{labels.invalidLink}</Paragraph>
        ) : (
          <Form layout="vertical" size="large" onFinish={(v) => void handleSubmit(v)} style={{ marginTop: 24 }}>
            <Form.Item
              name="password"
              label={labels.newPassword}
              rules={[{ required: true, message: labels.enterNewPassword }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="confirm"
              label={labels.confirmPassword}
              dependencies={['password']}
              rules={[{ required: true, message: labels.confirmYourPassword }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block style={buttonStyle}>
                {labels.savePassword}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
}

export default ResetPasswordPage;
